import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Camera,
  ClipboardList,
  HelpCircle,
  KeyRound,
  Mail,
  ShieldCheck,
  Trash2,
  User,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SecureImage } from '@/components/ui/SecureImage';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { LoadingBlock } from '@/components/ui/Loader';
import { QueryError } from '@/components/ui/QueryError';
import { PasswordStrength } from '@/features/auth/components/PasswordStrength';
import { Select } from '@/components/ui/Select';
import { BLOOD_GROUP_OPTIONS, GENDER_OPTIONS, type Option } from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { collectPages, EXPORT_PAGE_SIZE } from '@/lib/collectPages';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { capitalizeWords } from '@/lib/format';
import {
  ALL_PERMISSIONS,
  PERMISSION_LABEL,
  permissionGroupsFor,
  permissionGroupTitle,
} from '@/constants/permissions';
import { useAuthStore } from '@/features/auth/store/auth.store';
import type { InstituteRole } from '@/types/role';
import type { StaffMember } from '@/types/staff';
import { staffApi } from '../api/staff.api';
import {
  MARITAL_STATUS_OPTIONS,
  STAFF_STATUS_OPTIONS,
  STAFF_TYPE_HINT,
  STAFF_TYPE_OPTIONS,
} from '../constants';
import { NOT_SET_OPTION, StaffDetailsFields } from './StaffDetailsFields';
import {
  EMPTY_STAFF_FORM,
  STAFF_TYPES,
  createStaffFormResolver,
  staffFormResolver,
  staffSchema,
  toStaffDetailsFormValues,
  toStaffFormValues,
  type StaffFormValues,
} from '../schemas/staff.schema';
import { applyFieldErrors, toMobileDigits } from '@/lib/forms';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

export interface StaffFormModalProps {
  /** The account being edited, or null when the page is opening a new one. */
  member: StaffMember | null;
  isOpen: boolean;
  /** The institute's own roles, so one can be handed out. */
  roles: InstituteRole[];
  onClose: () => void;
  /** Gets the saved account back, so the page can confirm it by name. */
  onSaved: (saved: StaffMember, wasCreated: boolean) => void;
  /**
   * A new account opened from a role: that role is chosen up front, and the
   * staff type too when the role's kind is one. Both stay editable.
   */
  initialRole?: InstituteRole | null;
}

/**
 * The flag beside the fixed +91 prefix. Staff mobiles are Indian only — stored
 * as the ten digits, and sign-in, OTP and SMS all assume India — so the prefix
 * says so rather than offering countries that would never be saved.
 */
const INDIA_FLAG = (
  <svg className="w-5 h-3.5 rounded-xs shrink-0" viewBox="0 0 640 480" aria-hidden>
    <path fill="#ff9933" d="M0 0h640v160H0z" />
    <path fill="#ffffff" d="M0 160h640v160H0z" />
    <path fill="#128807" d="M0 320h640v160H0z" />
    <circle cx="320" cy="240" r="50" fill="none" stroke="#000080" strokeWidth="8" />
  </svg>
);

/**
 * A blank form, with the role it was opened from already chosen. A role kind
 * that is not a staff type (the school office) leaves the type as it was.
 */
function withInitialRole(role: InstituteRole | null): StaffFormValues {
  if (!role) return EMPTY_STAFF_FORM;
  const staffType = STAFF_TYPES.find((type) => type === role.kind);
  return {
    ...EMPTY_STAFF_FORM,
    roleUuid: role.uuid,
    ...(staffType ? { staffType } : {}),
  };
}

/** What the server's `uploadImage` filter accepts, mirrored for fast feedback. */
const PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PHOTO_MAX_MB = 2;

/**
 * Full page view for opening an account and editing one.
 */
export function StaffFormModal({
  member,
  isOpen,
  roles,
  onClose,
  onSaved,
  initialRole = null,
}: StaffFormModalProps) {
  const [formError, setFormError] = useState<string | null>(null);
  const [sendInvite, setSendInvite] = useState(true);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [removeExistingPhoto, setRemoveExistingPhoto] = useState(false);
  const [confirmingPhotoRemoval, setConfirmingPhotoRemoval] = useState(false);
  const [removingPhoto, setRemovingPhoto] = useState(false);
  /** The picked file itself. The preview above is a blob URL and cannot be sent. */
  const [photo, setPhoto] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = Boolean(member);

  /*
    The list row the form is opened from carries the account only — the
    profile's details are read one person at a time, never forty to a page. So
    editing loads the full record first, and the form waits for it: shown
    blank, a save would clear every detail the person already has.
  */
  const fullRecord = useApiQuery(
    () => (isOpen && member ? staffApi.get(member.uuid) : Promise.resolve(null)),
    [isOpen, member?.uuid],
  );
  const record = member ? fullRecord.data : null;
  const isRecordPending = Boolean(member) && !record;

  /*
    Who this person can report to: every active member of staff but themselves.
    Every page, not the first hundred, or a large school's principal could be
    missing from the list.
  */
  const colleagues = useApiQuery(
    () =>
      isOpen
        ? collectPages((page) => staffApi.list({ status: 'ACTIVE', page, limit: EXPORT_PAGE_SIZE }))
        : Promise.resolve(null),
    [isOpen],
  );
  const currentManager = record?.details?.reportingTo ?? null;
  const reportingOptions = useMemo<Option[]>(() => {
    const options = (colleagues.data?.items ?? [])
      .filter((colleague) => colleague.uuid !== member?.uuid)
      .map((colleague) => ({ value: colleague.uuid, label: colleague.fullName }));
    // A manager who has since been deactivated is still who they report to
    // until somebody changes it, so they stay choosable here.
    if (currentManager && !options.some((option) => option.value === currentManager.uuid)) {
      options.unshift({ value: currentManager.uuid, label: currentManager.fullName });
    }
    return options;
  }, [colleagues.data, member?.uuid, currentManager]);

  const form = useForm<StaffFormValues>({
    // A new account must state phone, role and date of joining; an edit need not.
    resolver: isEditing ? staffFormResolver : createStaffFormResolver,
    defaultValues: EMPTY_STAFF_FORM,
  });
  useLiveFormErrors(form);
  const {
    register,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  // Refilled whenever the view opens
  useEffect(() => {
    if (!isOpen) return;
    setFormError(null);
    setPhotoPreview(null);
    setPhoto(null);
    setRemoveExistingPhoto(false);
    setConfirmingPhotoRemoval(false);
  }, [isOpen, member]);

  // Filled from the full record once it is here, so the details are in it.
  useEffect(() => {
    if (!isOpen || (member && !record)) return;
    form.reset(
      record
        ? { ...toStaffFormValues(record), details: toStaffDetailsFormValues(record.details) }
        : withInitialRole(initialRole),
    );
  }, [isOpen, member, record, form, initialRole]);

  async function handleRemovePhoto() {
    if (!member) {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
      setPhoto(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setConfirmingPhotoRemoval(false);
      return;
    }

    setRemovingPhoto(true);
    try {
      await staffApi.removePhoto(member.uuid);
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
      setPhoto(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setRemoveExistingPhoto(true);
      member.hasPhoto = false;
      setConfirmingPhotoRemoval(false);
      toast.success('Photograph removed', `${member.fullName}'s photograph has been removed.`);
    } catch (caught) {
      toast.error(
        'Could not remove the photograph',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setRemovingPhoto(false);
    }
  }

  if (!isOpen) return null;

  const mobileField = register('mobile');

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Courtesy checks only — the server judges MIME type, extension and size
    // again, because anything the browser enforces can be skipped.
    if (!PHOTO_TYPES.includes(file.type)) {
      setFormError('Choose a PNG, JPG or WEBP image for the photograph');
      return;
    }
    if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
      setFormError(`That photograph is larger than ${PHOTO_MAX_MB} MB`);
      return;
    }

    setFormError(null);
    setPhoto(file);
    setRemoveExistingPhoto(false);
    setPhotoPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(file);
    });
  };

  // The system Administrator role belongs to the school's own administrator
  // account and the server refuses it for staff. Kept only for someone who
  // already holds it, so editing them does not silently blank their role.
  const roleOptions: Option[] = roles
    .filter((role) => !role.isSystem || role.uuid === member?.role?.uuid)
    .map((role) => ({ value: role.uuid, label: role.name }));

  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const email = watch('email');
  const mobile = watch('mobile');
  const staffType = watch('staffType');
  const roleUuid = watch('roleUuid');
  const joinedOn = watch('joinedOn');
  const password = watch('password') ?? '';
  /**
   * An existing account may be given a password only while it is switched on —
   * the server refuses it otherwise — so the fields go when Status is switched off.
   */
  const canSetPassword = isEditing && watch('status') === 'ACTIVE';

  // Hidden fields must not hold a half-typed password that blocks the save
  // with a strength error nobody can see.
  useEffect(() => {
    if (!isEditing || canSetPassword) return;
    form.resetField('password');
    form.resetField('confirmPassword');
  }, [isEditing, canSetPassword, form]);
  const confirmPassword = watch('confirmPassword') ?? '';

  const selectedRole = roles.find((r) => r.uuid === roleUuid);
  const roleName = selectedRole ? selectedRole.name : '—';
  // Previewed the way every list will print it once saved.
  const fullName = capitalizeWords([firstName, lastName].filter(Boolean).join(' ')) || '—';

  /**
   * Sends the picked photograph, if there is one, and returns the record the
   * caller should hand back.
   *
   * A photograph that fails on its own does not fail the save: the account *was*
   * written, and telling the office otherwise sends them to re-enter a form that
   * is already correct. They are told what happened and the record stands.
   */
  async function withPhoto(saved: StaffMember): Promise<StaffMember> {
    if (!photo) return saved;

    try {
      const withIt = await staffApi.uploadPhoto(saved.uuid, photo);
      setPhoto(null);
      return withIt;
    } catch (error) {
      toast.error(
        `${saved.fullName}'s details are saved, but the photograph is not`,
        error instanceof ApiRequestError ? error.message : 'Please try the photograph again.',
      );
      return saved;
    }
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);

    try {
      const { password: chosenPassword, confirmPassword: _confirm, ...parsed } = staffSchema.parse(values);
      // Masked numbers are a view, not values — sending them back would fail
      // validation at best. Left out, the server keeps what it has.
      const payload = record?.details?.isMasked ? { ...parsed, details: undefined } : parsed;

      // The photograph travels on its own route, after the account is saved:
      // it is bytes rather than a field, and a body the server rejects must not
      // leave a file stored against a record that never took the change.
      if (member) {
        let saved = await staffApi.update(member.uuid, payload);
        // After the details, so a refused edit never leaves the password changed
        // on its own. Blank means their password is left as it is.
        if (chosenPassword && canSetPassword) {
          await staffApi.setPassword(member.uuid, chosenPassword);
          form.resetField('password');
          form.resetField('confirmPassword');
        }
        if (removeExistingPhoto && member.hasPhoto && !photo) {
          saved = await staffApi.removePhoto(member.uuid);
        } else if (photo) {
          saved = await withPhoto(saved);
        }
        onSaved(saved, false);
      } else {
        const { status: _status, ...rest } = payload;
        const created = await staffApi.create({ ...rest, password: chosenPassword });
        onSaved(await withPhoto(created), true);
      }
    } catch (error) {
      if (error instanceof ApiRequestError) {
        applyFieldErrors(form.setError, error);
        setFormError(error.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <div className="w-full pb-16 transition-colors">
      {/* Top Breadcrumb & Page Header */}
      <div className="flex items-center gap-2 text-sm font-medium mb-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-slate-500 hover:text-indigo-600 hover:underline dark:text-slate-400 dark:hover:text-indigo-400 transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Staff</span>
        </button>
        <span className="text-slate-300 dark:text-slate-600 font-normal">/</span>
        <span className="text-slate-900 dark:text-white font-bold">
          {isEditing ? 'Edit staff member' : 'Add staff member'}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            {isEditing ? `Edit ${member?.fullName ?? 'staff member'}` : 'Add staff member'}
          </h1>
          <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">
            {isEditing
              ? 'Update staff member details, role, and access permissions.'
              : 'Add a new staff member and set their role, permissions and access.'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs sm:text-sm font-bold shadow-xs transition-all cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting || isRecordPending}
            className="inline-flex items-center justify-center px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs sm:text-sm font-bold shadow-md shadow-indigo-600/25 hover:shadow-lg transition-all cursor-pointer disabled:opacity-50"
          >
            <span>
              {isSubmitting
                ? isEditing
                  ? 'Saving…'
                  : 'Sending invite…'
                : isEditing
                  ? 'Save changes'
                  : 'Save staff member'}
            </span>
          </button>
        </div>
      </div>

      {formError && (
        <div className="mb-6">
          <Alert
            tone="critical"
            title={member ? 'Could not save this account' : 'Could not add this person'}
          >
            {formError}
          </Alert>
        </div>
      )}

      {isRecordPending && fullRecord.error ? (
        <QueryError
          title="Could not load this staff member"
          error={fullRecord.error}
          onRetry={fullRecord.refetch}
        />
      ) : isRecordPending ? (
        <LoadingBlock label="Loading this staff member…" />
      ) : (
      /* Main Grid: 8 cols Form Cards, 4 cols Summary Sidebar */
      <form onSubmit={onSubmit} noValidate className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 space-y-4">
          {/* Card 1: Personal Information */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-xs">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200/80 dark:border-emerald-800/80 text-emerald-600 dark:text-emerald-400 shrink-0">
                <User className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">
                  Personal information
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  Enter basic details about the staff member.
                </p>
              </div>
            </div>

            {/* Top Grid: Rows 1 & 2 on the left (md:col-span-9), Photo Upload on the right (md:col-span-3) */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5 items-stretch">
              <div className="md:col-span-9 space-y-3.5">
                {/* Row 1: First Name & Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <Input
                    label="First name"
                    required
                    placeholder="e.g. Ramesh"
                    autoComplete="off"
                    error={errors.firstName?.message}
                    {...register('firstName')}
                  />
                  <Input
                    label="Last name"
                    required
                    placeholder="e.g. Kumar"
                    autoComplete="off"
                    error={errors.lastName?.message}
                    {...register('lastName')}
                  />
                </div>

                {/* Row 2: Email Address & Phone Number (with Country Code) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <Input
                    label="Email address"
                    required
                    type="email"
                    placeholder="e.g. ramesh.kumar@example.com"
                    autoComplete="off"
                    leftIcon={<Mail className="h-4 w-4" />}
                    error={errors.email?.message}
                    {...register('email')}
                  />
                  <div>
                    <label className="lbl mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
                      Phone number <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <div className="flex items-start gap-1.5">
                      <span
                        className={cn(
                          'h-10 rounded-field border bg-surface px-2.5 flex items-center gap-1.5 text-xs font-bold text-ink shrink-0',
                          errors.mobile?.message ? 'border-crit' : 'border-line-strong',
                        )}
                        title="India (+91)"
                      >
                        {INDIA_FLAG}
                        <span className="font-mono">+91</span>
                      </span>
                      <div className="flex-1 min-w-0">
                        <Input
                          hideLabel
                          label="Phone number"
                          required
                          inputMode="numeric"
                          autoComplete="off"
                          placeholder="9876543210"
                          error={errors.mobile?.message}
                          {...mobileField}
                          onChange={(event) => {
                            event.target.value = toMobileDigits(event.target.value);
                            void mobileField.onChange(event);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Photo Upload Dotted Box (Covers only the top 2 rows) */}
              <div
                className="relative md:col-span-3 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-3 bg-slate-50/50 dark:bg-slate-800/40 text-center hover:border-emerald-500 transition-colors h-full min-h-[140px]"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                {photoPreview ? (
                  <div className="flex flex-col items-center gap-2 py-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (photoPreview) URL.revokeObjectURL(photoPreview);
                        setPhotoPreview(null);
                        setPhoto(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="absolute top-2 right-2 p-1 rounded-md text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors cursor-pointer"
                      title="Remove photo"
                    >
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </button>
                    <img
                      src={photoPreview}
                      alt="Staff preview"
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-emerald-500 shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="inline-flex items-center justify-center px-3 py-1 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:border-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 transition-all shadow-xs cursor-pointer"
                    >
                      Change photo
                    </button>
                  </div>
                ) : member?.hasPhoto && !removeExistingPhoto ? (
                  <div className="flex flex-col items-center gap-2 py-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmingPhotoRemoval(true);
                      }}
                      className="absolute top-2 right-2 p-1 rounded-md text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors cursor-pointer"
                      title="Remove photo"
                    >
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </button>
                    <div className="w-16 h-16 sm:w-20 sm:h-20 overflow-hidden rounded-full border-2 border-emerald-500 shadow-sm">
                      <SecureImage
                        path={staffApi.photoPath(member.uuid)}
                        alt={member.fullName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="inline-flex items-center justify-center px-3 py-1 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:border-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 transition-all shadow-xs cursor-pointer"
                    >
                      Change photo
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center cursor-pointer group py-2"
                  >
                    <div className="w-11 h-11 rounded-full bg-slate-200/80 dark:bg-slate-700/80 text-slate-500 dark:text-slate-400 flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform">
                      <Camera className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                      Upload photo
                    </span>
                    <span className="text-[10px] text-slate-400 mt-0.5">JPG, PNG up to 2MB</span>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Section: Expanded Rows 3 & 4 (Full Width across all 12 columns) */}
            <div className="mt-3.5 space-y-3.5">
              {/* Row 3: Employee Code, Date of birth & Gender */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                <Input
                  label="Employee ID"
                  className="font-mono"
                  readOnly
                  value={member?.employeeCode ?? ''}
                  placeholder="e.g. EMP/2026/0001"
                  hint={
                    member?.employeeCode ? undefined : (
                      <>
                        Issued automatically on save
                        <br />
                        Format: EMP/2026/0001
                      </>
                    )
                  }
                />
                
                <DatePicker
                  label="Date of birth"
                  required={!isEditing}
                  placeholder="Select date"
                  error={errors.details?.dateOfBirth?.message}
                  {...register('details.dateOfBirth')}
                />
                <Select
                  label="Gender"
                  required={!isEditing}
                  options={[NOT_SET_OPTION, ...GENDER_OPTIONS]}
                  error={errors.details?.gender?.message}
                  {...register('details.gender')}
                />
              </div>

              {/* Row 4: Blood group, Marital status & Nationality */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                <Select
                  label="Blood group"
                  options={[NOT_SET_OPTION, ...BLOOD_GROUP_OPTIONS]}
                  error={errors.details?.bloodGroup?.message}
                  {...register('details.bloodGroup')}
                />
                <Select
                  label="Marital status"
                  options={[NOT_SET_OPTION, ...MARITAL_STATUS_OPTIONS]}
                  error={errors.details?.maritalStatus?.message}
                  {...register('details.maritalStatus')}
                />
                <Input
                  label="Nationality"
                  placeholder="e.g. Indian"
                  autoComplete="off"
                  error={errors.details?.nationality?.message}
                  {...register('details.nationality')}
                />
              </div>
            </div>
          </div>

          {/* Card 2: Work Information */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-xs">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/60 border border-blue-200/80 dark:border-blue-800/80 text-blue-600 dark:text-blue-400 shrink-0">
                <Briefcase className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">
                  Work information
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  Assign role, staff type and related work details.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              <Select
                label="Role"
                required
                options={roleOptions}
                placeholder="Select role"
                error={errors.roleUuid?.message}
                {...register('roleUuid')}
              />
              <Select
                label="Staff type"
                required
                options={STAFF_TYPE_OPTIONS}
                hint={STAFF_TYPE_HINT[staffType as keyof typeof STAFF_TYPE_HINT]}
                error={errors.staffType?.message}
                {...register('staffType')}
              />
              <Input
                label="Designation"
                required={!isEditing}
                placeholder="e.g. Senior Teacher"
                error={errors.designation?.message}
                {...register('designation')}
              />
              <DatePicker
                label="Date of joining"
                required
                placeholder="Select date"
                error={errors.joinedOn?.message}
                {...register('joinedOn')}
              />
              <Input
                label="Qualification"
                placeholder="e.g. M.Sc, B.Ed"
                autoComplete="off"
                error={errors.details?.qualification?.message}
                {...register('details.qualification')}
              />
              <Input
                label="Experience (years)"
                placeholder="e.g. 5"
                inputMode="decimal"
                autoComplete="off"
                error={errors.details?.experienceYears?.message}
                {...register('details.experienceYears')}
              />
            </div>
          </div>

          <StaffDetailsFields
            register={register}
            errors={errors}
            reportingOptions={reportingOptions}
            isLoadingReporting={colleagues.isLoading}
            isEnrolling={!isEditing}
            salaryPaymentMode={watch('details.salaryPaymentMode')}
          />

          {/* Card 3: Account Information */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-xs">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="p-2 rounded-xl bg-purple-50 dark:bg-purple-950/60 border border-purple-200/80 dark:border-purple-800/80 text-purple-600 dark:text-purple-400 shrink-0">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">
                  Account information
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  Set login access for the staff member.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              <Input
                label="Login email"
                required
                type="email"
                value={email || ''}
                readOnly
                placeholder="e.g. ramesh@gmail.com"
                className="bg-slate-50 dark:bg-slate-800 cursor-not-allowed"
                hint="This will be used to login to the system."
              />
              {isEditing && (
                <Select
                  label="Account status"
                  options={STAFF_STATUS_OPTIONS}
                  hint="Suspending signs them out and blocks sign-in."
                  error={errors.status?.message}
                  {...register('status')}
                />
              )}
              {(!isEditing || canSetPassword) && (
                <>
                  <Input
                    label={isEditing ? 'New password' : 'Password'}
                    isPassword
                    autoComplete="new-password"
                    placeholder={isEditing ? 'Enter a new password' : 'Enter password'}
                    hint={
                      isEditing
                        ? 'Leave blank to keep their current password.'
                        : 'Leave blank to email them a link to set their own.'
                    }
                    error={errors.password?.message}
                    {...register('password', {
                      // Judged as it is typed rather than on save, and the
                      // confirmation with it, so a mismatch clears the moment
                      // the first field is corrected.
                      onChange: () =>
                        void form.trigger(confirmPassword ? ['password', 'confirmPassword'] : 'password'),
                    })}
                  />
                  {/* Typed, never pasted: a confirmation copied from the box
                      above repeats whatever typo is in it, which is the one
                      thing this field exists to catch. */}
                  <Input
                    label="Confirm password"
                    isPassword
                    autoComplete="new-password"
                    placeholder="Confirm password"
                    hint="Type the password again — pasting is turned off."
                    error={errors.confirmPassword?.message}
                    onPaste={(event) => event.preventDefault()}
                    onDrop={(event) => event.preventDefault()}
                    {...register('confirmPassword', {
                      onChange: () => void form.trigger('confirmPassword'),
                    })}
                  />
                </>
              )}
            </div>

            {(!isEditing || canSetPassword) && (password || confirmPassword) && (
              <div className="mt-3.5 rounded-2xl border border-line bg-surface-2 p-3.5">
                <PasswordStrength value={password} confirmation={confirmPassword} />
                <p className="mt-3 text-xs text-ink-3">
                  They will be asked to change it the first time they sign in.
                  {isEditing && ' Saving signs them out everywhere and cancels any emailed link.'}
                </p>
              </div>
            )}

            <div className="mt-3.5 flex items-center gap-2">
              <input
                type="checkbox"
                id="sendInvite"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="rounded border-slate-300 dark:border-slate-700 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
              />
              <label
                htmlFor="sendInvite"
                className="text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer"
              >
                Send login credentials to this email address
              </label>
            </div>

          </div>

          {/* Card 4: Permissions & Access */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3.5">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/60 border border-amber-200/80 dark:border-amber-800/80 text-amber-600 dark:text-amber-400 shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">
                    Permissions & access
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    Decided by their role, set on the Roles screen.
                  </p>
                </div>
              </div>
            </div>

            {/* Read-only on purpose. Access belongs to the role, not to the person:
                every member holding a role gets exactly its permissions (narrowed
                by the features the school has switched on). A different set for
                one person is a new role, made on the Roles screen. */}
            <RoleAccessSummary role={selectedRole} />
          </div>
        </div>

        {/* Right Column Sidebar: Staff Summary & Help */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs sticky top-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-teal-50 dark:bg-teal-950/60 border border-teal-200/80 dark:border-teal-800/80 text-teal-600 dark:text-teal-400 shrink-0">
                <ClipboardList className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                  Staff summary
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  Review the details before saving.
                </p>
              </div>
            </div>

            <div className="space-y-3 divide-y divide-slate-100 dark:divide-slate-800 text-xs sm:text-sm">
              <div className="pt-2 flex justify-between gap-2">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Name</span>
                <span className="font-bold text-slate-900 dark:text-white text-right truncate">
                  {fullName}
                </span>
              </div>
              <div className="pt-2 flex justify-between gap-2">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Role</span>
                <span className="font-bold text-slate-900 dark:text-white text-right truncate">
                  {roleName}
                </span>
              </div>
              <div className="pt-2 flex justify-between gap-2">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Email</span>
                <span className="font-bold text-slate-900 dark:text-white text-right truncate max-w-[150px]">
                  {email || '—'}
                </span>
              </div>
              <div className="pt-2 flex justify-between gap-2">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Phone</span>
                <span className="font-bold text-slate-900 dark:text-white text-right truncate">
                  {mobile || '—'}
                </span>
              </div>
              <div className="pt-2 flex justify-between gap-2">
                <span className="text-slate-500 dark:text-slate-400 font-medium">
                  Date of joining
                </span>
                <span className="font-bold text-slate-900 dark:text-white text-right truncate">
                  {joinedOn || '—'}
                </span>
              </div>
              <div className="pt-2 flex justify-between gap-2">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Access</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400 text-right truncate">
                  {accessLabel(selectedRole)}
                </span>
              </div>
            </div>

            {/* Need Help Box */}
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/50 rounded-xl p-3.5">
              <h3 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white">
                Need help?
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Learn more about managing staff.
              </p>
              <button
                type="button"
                className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-bold text-emerald-700 dark:text-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors w-full justify-center cursor-pointer"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                <span>View help guide</span>
              </button>
            </div>
          </div>
        </div>
      </form>
      )}

      <ConfirmDialog
        isOpen={confirmingPhotoRemoval}
        title="Remove this photograph?"
        description={`${fullName || 'This staff member'}'s photograph will be deleted and their initials shown instead.`}
        confirmLabel="Remove photo"
        tone="danger"
        isLoading={removingPhoto}
        onConfirm={handleRemovePhoto}
        onCancel={() => setConfirmingPhotoRemoval(false)}
      />
    </div>
  );
}

/** "Full access", "12 permissions", or a dash before a role is chosen. */
function accessLabel(role: InstituteRole | undefined): string {
  if (!role) return '—';
  if (ALL_PERMISSIONS.every((permission) => role.permissions.includes(permission))) return 'Full access';
  return `${role.permissions.length} permission${role.permissions.length === 1 ? '' : 's'}`;
}

/**
 * What the chosen role lets this person do, grouped the way the Roles screen
 * groups it — read-only, with the way to change it one click away.
 */
function RoleAccessSummary({ role }: { role: InstituteRole | undefined }) {
  if (!role) {
    return (
      <p className="rounded-field border border-dashed border-line-strong bg-surface-2 px-4 py-3 text-sm text-ink-3">
        Choose a role above to see what this person will be able to do.
      </p>
    );
  }

  const schoolFeatures = useAuthStore((state) => state.user?.features);
  const featureNames = useAuthStore((state) => state.user?.featureNames);
  const groups = permissionGroupsFor(schoolFeatures).map((group) => ({
    title: permissionGroupTitle(group, featureNames),
    held: group.permissions.filter((permission) => role.permissions.includes(permission)),
  })).filter((group) => group.held.length > 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-field border border-line bg-surface-2 px-4 py-3">
        <p className="text-sm text-ink">
          <span className="font-[650]">{role.name}</span>
          <span className="text-ink-3"> · {accessLabel(role)}</span>
        </p>
        <Link
          to="/admin/roles"
          className="inline-flex items-center gap-1 rounded-field text-xs font-[560] text-accent hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
        >
          Change in Roles
          <ArrowRight className="h-3 w-3" aria-hidden />
        </Link>
      </div>

      {groups.length === 0 ? (
        <p className="text-sm text-ink-3">This role has no permissions yet — the person can sign in but see little.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {groups.map((group) => (
            <li key={group.title} className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
              <span className="w-40 shrink-0 text-xs font-[650] text-ink-2">{group.title}</span>
              <span className="flex flex-wrap gap-1">
                {group.held.map((permission) => (
                  <span key={permission} className="pill bg-accent-soft text-accent">
                    {PERMISSION_LABEL[permission]}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="text-2xs text-ink-3">
        Features your school has not switched on are left out, whatever the role allows.
      </p>
    </div>
  );
}
